"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useJsonResource } from "@/hooks/useJsonResource";
import { requestJson } from "@/lib/clientRequest";
import TestCoverageLine, { type TestCoverage } from "@/components/TestCoverageLine";

type Setup = { workerName: string; revisionId: string | null; draft: boolean; tested: boolean; canActivate: boolean; triggerSummary: string[]; coverage?: TestCoverage | null };

export default function ChatApprovalControl({ workerId, revisionId }: { workerId: string; revisionId: string }) {
  const { data, error: loadError, refresh } = useJsonResource<Setup>(`/api/workers/${workerId}/setup`);
  const router = useRouter();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const eligible = data?.revisionId === revisionId && data.draft && data.tested && data.canActivate;
  async function approve() {
    setBusy(true);
    setError("");
    try {
      await requestJson(`/api/workers/${workerId}/setup`, {
        method: "POST", body: { action: "approve", revisionId }, fallback: "Could not activate this revision",
      });
      await refresh();
      router.refresh();
      window.dispatchEvent(new Event("caw:activity-refresh"));
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Could not activate this revision"); }
    finally { setBusy(false); }
  }
  if (!data) return <span className="block text-xs text-zinc-400">{loadError ?? "Loading approval"}</span>;
  return <span className="mt-2 block border-t border-zinc-700 pt-2 text-xs">
    <span className="block font-semibold text-zinc-100">{data.workerName}</span>
    <span className="mb-2 block text-zinc-400">{data.triggerSummary.length ? data.triggerSummary.join(", ") : "Manual runs only"}</span>
    {eligible && <TestCoverageLine coverage={data.coverage} inline />}
    {eligible ? <button type="button" disabled={busy} onClick={() => void approve()}
      className="rounded bg-violet-600 px-3 py-1.5 font-medium text-white disabled:opacity-50">Approve and activate this revision</button>
      : <span className="text-zinc-400">This revision is no longer eligible for approval.</span>}
    {error && <span role="alert" className="mt-2 block text-red-300">{error}</span>}
  </span>;
}
